import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

interface ProfileSkill {
  id: string;
  skill: string;
  skillNormalized: string;
  category: string;
}

interface WorkExperience {
  title: string;
  company: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

interface ProfileDetails {
  profileText: string | null;
  workExperience: WorkExperience[];
  education: Array<{ institution: string; degree: string; year?: string }>;
  certifications: string[];
}

interface MyProfile {
  id: string;
  name: string;
  email: string | null;
  location: string | null;
  summary: string | null;
  seniority: string | null;
  skills: ProfileSkill[];
  details: ProfileDetails | null;
}

export function useMyProfile() {
  return useQuery({
    queryKey: ['my-profile'],
    queryFn: () => apiFetch<MyProfile | null>('/profiles/me')
  });
}

export function useUploadCv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileBuffer, fileName }: { fileBuffer: string; fileName: string }) =>
      apiFetch<{ profileId?: string; extracted?: Record<string, unknown> }>('/profiles/upload-cv', {
        method: 'POST',
        body: JSON.stringify({ fileBuffer, fileName })
      }),
    onSuccess: () => {
      // Invalidate immediately — profile will show current state
      // User can click Refresh after task completes
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    }
  });
}

export function useApplyCv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (extracted: Record<string, unknown>) =>
      apiFetch<{ profileId: string }>('/profiles/apply-cv', {
        method: 'POST',
        body: JSON.stringify({ extracted })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    }
  });
}

export function useDeleteSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) =>
      apiFetch<null>(`/profiles/me/skills/${skillId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    }
  });
}

export function useDeleteExperience() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (index: number) =>
      apiFetch<null>(`/profiles/me/experience/${index}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    }
  });
}

export type { MyProfile, ProfileSkill, WorkExperience, ProfileDetails };
